// Pre-publication story review, opened from a review link
// (`/review/{token}`). Rendered OUTSIDE the auth gate (see App.js) and
// talks only to the two public endpoints:
//   GET  /api/share/story/{token}            story + comments on this link
//   POST /api/share/story/{token}/comments   add a comment
//
// Read-only for the story: no edit affordance exists here or in the API.
// Comments are plain text, rendered as text (React escapes them). The
// reviewer can select a passage and "Comment on selection" to attach
// the quoted text to their note.
//
// Search engines: the page adds <meta name="robots" content="noindex">
// and the API answers with X-Robots-Tag: noindex. A no-referrer policy
// keeps the token out of the Referer header when the reviewer clicks a
// link inside the story.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, MessageSquare, Quote, Send, X, CheckCircle2,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const NAME_KEY = "newsdesk.review.name";

async function call(path, opts = {}) {
  // `credentials: "omit"` — this page must work with no session, and
  // must never send an editor's cookie that happens to exist.
  const res = await fetch(`${BASE}/api/share/story/${path}`, {
    credentials: "omit",
    cache: "no-store",
    ...opts,
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.detail || "";
    } catch {
      /* non-JSON body */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function useHeadMeta(name, content) {
  useEffect(() => {
    const el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute("content", content);
    document.head.appendChild(el);
    return () => {
      try {
        document.head.removeChild(el);
      } catch {
        /* already gone */
      }
    };
  }, [name, content]);
}

function readName() {
  try {
    return window.localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
}

function saveName(v) {
  try {
    window.localStorage.setItem(NAME_KEY, v);
  } catch {
    /* private mode */
  }
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

// Same paragraph split the editor view uses, so body images land after
// the same paragraph they sit after in Newsdesk.
function splitParagraphs(html) {
  const parts = [];
  const re = /<p\b[^>]*>[\s\S]*?<\/p\s*>/gi;
  let last = 0;
  let m;
  let buffer = "";
  while ((m = re.exec(html)) !== null) {
    buffer += html.slice(last, m.index);
    parts.push(buffer + m[0]);
    buffer = "";
    last = m.index + m[0].length;
  }
  const trailing = buffer + html.slice(last);
  if (trailing.trim()) parts.push(trailing);
  return parts.length ? parts : [html];
}

function imgSrc(url) {
  if (!url) return "";
  return url.startsWith("/") ? `${BASE}${url}` : url;
}

function Figure({ url, alt, credit }) {
  return (
    <figure className="my-6">
      <img
        src={imgSrc(url)}
        alt={alt || ""}
        className="w-full h-auto border border-[var(--border)]"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
      {credit && (
        <figcaption className="text-[11px] text-[var(--ink-muted)] mt-1 font-mono-display">
          {credit}
        </figcaption>
      )}
    </figure>
  );
}

export default function PublicStoryReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState(readName);
  const [text, setText] = useState("");
  const [quote, setQuote] = useState("");
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");
  const [sentFlash, setSentFlash] = useState(false);
  const [selection, setSelection] = useState("");

  const storyRef = useRef(null);
  const formRef = useRef(null);
  const textRef = useRef(null);

  useHeadMeta("robots", "noindex, nofollow, noarchive");
  useHeadMeta("referrer", "no-referrer");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    call(encodeURIComponent(token))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    document.title = data?.story?.headline
      ? `Review: ${data.story.headline}`
      : "Story review";
  }, [data]);

  // Track a text selection inside the story so the reviewer can quote it.
  useEffect(() => {
    const onSel = () => {
      const sel = window.getSelection ? window.getSelection() : null;
      const root = storyRef.current;
      if (!sel || sel.isCollapsed || !root || sel.rangeCount === 0) {
        setSelection("");
        return;
      }
      const node = sel.getRangeAt(0).commonAncestorContainer;
      if (!root.contains(node)) {
        setSelection("");
        return;
      }
      const t = sel.toString().replace(/\s+/g, " ").trim();
      setSelection(t.slice(0, data?.limits?.quote || 500));
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, [data]);

  const applySelection = useCallback(() => {
    if (!selection) return;
    setQuote(selection);
    setSelection("");
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      /* ignore */
    }
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => textRef.current?.focus(), 350);
  }, [selection]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!name.trim()) {
      setFormError("Please enter your name.");
      return;
    }
    if (!text.trim()) {
      setFormError("The comment is empty.");
      return;
    }
    setSending(true);
    try {
      const res = await call(`${encodeURIComponent(token)}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          author_name: name.trim(),
          text,
          quote: quote || null,
        }),
      });
      saveName(name.trim());
      setData((d) =>
        d ? { ...d, comments: [...(d.comments || []), res.comment] } : d,
      );
      setText("");
      setQuote("");
      setSentFlash(true);
      setTimeout(() => setSentFlash(false), 4000);
    } catch (err) {
      setFormError(err.message || "Could not send the comment.");
    } finally {
      setSending(false);
    }
  };

  const story = data?.story;
  const paragraphs = useMemo(
    () => splitParagraphs(story?.body_html || ""),
    [story?.body_html],
  );
  const imagesByIdx = useMemo(() => {
    const m = new Map();
    (story?.body_images || []).forEach((img) => {
      const idx = Math.min(paragraphs.length, Math.max(0, img.paragraph_index || 0));
      if (!m.has(idx)) m.set(idx, []);
      m.get(idx).push(img);
    });
    return m;
  }, [story?.body_images, paragraphs.length]);

  if (loading && !data) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[var(--bg)]">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--ink-muted)]" />
      </div>
    );
  }

  if (error && !data) {
    const gone = error.status === 404;
    return (
      <div
        data-testid="story-review-error"
        className="min-h-screen w-full flex items-center justify-center bg-[var(--bg)] px-6"
      >
        <div className="max-w-md text-center">
          <div className="overline mb-2">Story review</div>
          <h1 className="font-serif-display text-2xl mb-3">
            {gone ? "This review link is no longer valid." : "Couldn't load this story."}
          </h1>
          <p className="text-sm text-[var(--ink-muted)]">
            {gone
              ? "The link may have expired or been withdrawn. Ask your contact at the publication for a new one."
              : error.message}
          </p>
        </div>
      </div>
    );
  }

  const comments = data?.comments || [];
  const limits = data?.limits || { name: 80, comment: 4000 };

  return (
    <div
      data-testid="story-review"
      className="min-h-screen w-full bg-[var(--bg)] overflow-y-auto"
    >
      <div className="border-b border-[var(--border)] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="overline">{data?.property_name || "Story review"}</div>
          <span className="text-[10px] uppercase tracking-wider font-mono-display px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">
            Draft for review · not published
          </span>
          {data?.expires_at && (
            <span className="text-[10px] text-[var(--ink-muted)] font-mono-display ml-auto">
              Link expires {new Date(data.expires_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-xs text-[var(--ink-muted)] mb-6 leading-snug">
          You've been asked to review this story before it is published.
          Please don't share this link. Leave your notes at the bottom of
          the page — to comment on a specific passage, select it and tap
          "Comment on selection".
        </p>

        <article ref={storyRef} data-testid="story-review-article">
          <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-3">
            {story?.headline}
          </h1>
          {story?.dek && (
            <p className="text-lg text-[var(--ink-muted)] leading-snug mb-3">
              {story.dek}
            </p>
          )}
          {story?.writer_name && (
            <div className="overline text-[var(--ink-muted)] mb-6">
              By {story.writer_name}
            </div>
          )}
          {story?.featured_image_url && (
            <Figure
              url={story.featured_image_url}
              alt={story.headline}
              credit={story.featured_image_credit}
            />
          )}
          <div className="prose-editorial">
            {paragraphs.map((html, i) => (
              <div key={i}>
                <div dangerouslySetInnerHTML={{ __html: html }} />
                {(imagesByIdx.get(i) || []).map((img, j) => (
                  <Figure key={`${i}-${j}`} {...img} />
                ))}
              </div>
            ))}
            {(imagesByIdx.get(paragraphs.length) || []).map((img, j) => (
              <Figure key={`tail-${j}`} {...img} />
            ))}
          </div>
        </article>

        <section
          className="mt-12 border-t border-[var(--border)] pt-6"
          data-testid="story-review-comments"
        >
          <h2 className="font-serif-display text-xl mb-4 inline-flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[var(--accent)]" />
            Comments
            {comments.length > 0 && (
              <span className="text-sm text-[var(--ink-muted)]">· {comments.length}</span>
            )}
          </h2>

          {comments.length === 0 ? (
            <p className="text-sm text-[var(--ink-muted)] mb-6">No comments yet.</p>
          ) : (
            <ul className="space-y-3 mb-6">
              {comments.map((c) => (
                <li
                  key={c.id}
                  data-testid="story-review-comment"
                  className="bg-white border border-[var(--border)] p-3 text-sm"
                >
                  <div className="flex items-center gap-2 text-[11px] font-mono-display text-[var(--ink-muted)] mb-1">
                    <span className="font-medium text-[var(--ink)]">{c.author_name}</span>
                    <span>{fmtDateTime(c.created_at)}</span>
                    {c.resolved && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" /> addressed
                      </span>
                    )}
                  </div>
                  {c.quote && (
                    <blockquote className="border-l-2 border-[var(--accent)] pl-2 my-1 text-xs italic text-[var(--ink-muted)]">
                      “{c.quote}”
                    </blockquote>
                  )}
                  <div className="whitespace-pre-wrap leading-relaxed">{c.text}</div>
                </li>
              ))}
            </ul>
          )}

          <form
            ref={formRef}
            onSubmit={submit}
            data-testid="story-review-form"
            className="bg-white border border-[var(--border)] p-4 space-y-3"
          >
            <div className="overline">Add a comment</div>
            <input
              data-testid="story-review-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={limits.name}
              className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--ink)]"
            />
            {quote && (
              <div className="flex items-start gap-2 text-xs bg-[var(--secondary)] border border-[var(--border)] p-2">
                <Quote className="w-3 h-3 mt-0.5 shrink-0 text-[var(--accent)]" />
                <span className="flex-1 italic text-[var(--ink-muted)]">“{quote}”</span>
                <button
                  type="button"
                  onClick={() => setQuote("")}
                  aria-label="Remove quote"
                  className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <textarea
              ref={textRef}
              data-testid="story-review-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              maxLength={limits.comment}
              placeholder="Corrections, questions, suggestions…"
              className="w-full border border-[var(--border)] bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[var(--ink)] resize-y"
            />
            {formError && (
              <div className="text-xs text-red-700" data-testid="story-review-form-error">
                {formError}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                data-testid="story-review-submit"
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Send comment
              </button>
              {sentFlash && (
                <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Sent — the editors can see it now.
                </span>
              )}
            </div>
          </form>
        </section>
      </div>

      {selection && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
          <button
            type="button"
            data-testid="story-review-quote-selection"
            // mousedown, not click: a click would clear the selection
            // before the handler reads it on some browsers.
            onMouseDown={(e) => {
              e.preventDefault();
              applySelection();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              applySelection();
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[var(--slate-900)] text-white shadow-lg"
          >
            <Quote className="w-3.5 h-3.5" /> Comment on selection
          </button>
        </div>
      )}
    </div>
  );
}